/**
 * When the web stage stops floating the self-view.
 *
 * With one to five remote signers the self-view floats as a movable inset;
 * the three-column grid leaves its bottom-right cell empty in every case
 * but three and six, and at three the letterboxed video clears the inset.
 * At six remote tiles (3 × 2) the inset sits on the sixth signer's hands at
 * 1366 × 768, and from six on the tiles are small enough that a grid cell
 * costs the others almost nothing: 7 or 8 tiles in three columns are
 * 427 × 240 video each at 1366 × 768, against 439 × 247 for six. So the
 * self-view takes a cell, after everyone else.
 *
 * The web stage only. On the native stage your own video always floats over
 * the grid (Robert, 2026-10-05), unless Split gives it a cell.
 */
export const SELF_VIEW_DOCK_MIN_REMOTES = 6;
/**
 * Whether the self-view takes a grid cell instead of floating (web stage).
 *
 * @param {number} remoteCount - Remote participants with a tile.
 * @returns {boolean}
 */
export function selfViewDocksInGrid(remoteCount) {
    return remoteCount >= SELF_VIEW_DOCK_MIN_REMOTES;
}
/**
 * From this many remote tiles a tablet's native stage is a grid of equal
 * tiles instead of the one-, two- and three-pane arrangements
 * (VRSLayout.native.tsx).
 */
export const STAGE_GRID_MIN_REMOTES = 3;
/**
 * A phone's native stage is a grid from two remote tiles: a call of three.
 * Robert, 2026-10-05: the uneven split of a call of three is gone; every
 * remote tile is a cell of the same size, and your own video floats.
 */
export const PHONE_STAGE_GRID_MIN_REMOTES = 2;
/**
 * Whether the native stage is a grid.
 *
 * @param {number} remoteCount - Remote tiles on the stage (a pane held for
 * someone on the way counts).
 * @param {boolean} phone - The short side of the screen is under 600 points.
 * @returns {boolean}
 */
export function usesStageGrid(remoteCount, phone) {
    return remoteCount >= (phone ? PHONE_STAGE_GRID_MIN_REMOTES : STAGE_GRID_MIN_REMOTES);
}
/** The shape of an upright phone camera's picture. */
export const PORTRAIT_VIDEO_ASPECT = 9 / 16;
/** The shape of a camera's picture held sideways. */
export const LANDSCAPE_VIDEO_ASPECT = 16 / 9;
/**
 * Two plans whose cells differ in shape by less than this are equally close
 * to the video (the difference of the natural logs of the two shapes, 0.02
 * is 2 %). Then the larger cell wins.
 */
const SAME_SHAPE = 0.02;
/**
 * A shape (width / height) that a picture can have.
 *
 * @param {number} value - The number to check.
 * @returns {boolean}
 */
function isPositive(value) {
    return typeof value === 'number' && Number.isFinite(value) && value > 0;
}
/**
 * The typical shape of the remote videos on the stage: the median of the
 * last shapes their renderers reported. With no known shape, the shape of a
 * camera held the same way as this screen.
 *
 * @param {Array<number|undefined>} aspects - The last known shape (width /
 * height) of each remote video; undefined where it is not known.
 * @param {boolean} landscape - The screen is wider than it is tall.
 * @returns {number}
 */
export function typicalVideoAspect(aspects, landscape) {
    const known = aspects.filter(isPositive).sort((left, right) => left - right);
    if (!known.length) {
        return landscape ? LANDSCAPE_VIDEO_ASPECT : PORTRAIT_VIDEO_ASPECT;
    }
    const middle = Math.floor(known.length / 2);
    return known.length % 2 ? known[middle] : Math.sqrt(known[middle - 1] * known[middle]);
}
/**
 * Columns and rows for `count` equal tiles in a `width` × `height` stage.
 *
 * Each video fills its cell (cover), so the plan with cells closest to the
 * shape of the video cuts the least off each picture. The candidates are the
 * grids that need every row and every column: empty cells are only in the
 * last row, and the stage centres that row. Of two plans equally close in
 * shape (SAME_SHAPE), the one with the larger cells wins.
 *
 * @param {Object} options - The stage and the tiles.
 * @param {number} options.count - Tiles to place.
 * @param {number} options.gap - Space between tiles in points.
 * @param {number} options.height - Stage height in points.
 * @param {number} options.videoAspect - The typical shape (width / height) of
 * the videos (typicalVideoAspect). Without one, the stage's own orientation.
 * @param {number} options.width - Stage width in points.
 * @returns {IStageGridPlan}
 */
export function planStageGrid({ count, gap = 4, height, videoAspect, width }) {
    const tiles = Math.max(1, Math.floor(count) || 1);
    const stageWidth = Math.max(0, width || 0);
    const stageHeight = Math.max(0, height || 0);
    const aspect = isPositive(videoAspect)
        ? videoAspect
        : stageWidth > stageHeight ? LANDSCAPE_VIDEO_ASPECT : PORTRAIT_VIDEO_ASPECT;
    const candidates = [];
    for (let columns = 1; columns <= tiles; columns++) {
        const rows = Math.ceil(tiles / columns);
        // Not a candidate if a column holds no tile: the same rows hold every
        // tile with fewer columns.
        if (Math.ceil(tiles / rows) === columns) {
            const cellWidth = Math.max(0, (stageWidth - (gap * (columns - 1))) / columns);
            const cellHeight = Math.max(0, (stageHeight - (gap * (rows - 1))) / rows);
            candidates.push({
                area: cellWidth * cellHeight,
                columns,
                miss: cellWidth > 0 && cellHeight > 0 ? Math.abs(Math.log(cellWidth / cellHeight / aspect)) : Infinity,
                rows
            });
        }
    }
    const closest = Math.min(...candidates.map(candidate => candidate.miss));
    const equallyClose = candidates.filter(candidate => candidate.miss <= closest + SAME_SHAPE);
    let best = equallyClose[0];
    for (const candidate of equallyClose) {
        if (candidate.area > best.area) {
            best = candidate;
        }
    }
    return {
        columns: best.columns,
        rows: best.rows
    };
}
